import { inject } from '@angular/core';
import { Store } from '@ngrx/store';

import {
  type CanvasElementFrom,
  type PartialPropsOf,
  type PropsOf,
  type WithProps,
} from '@blaze-runtime/engine-domain';
import { type FontWeightOption } from '@blaze-shared/api-contracts-domain';
import { type PropertiesConfig, type RenderElementContext } from '@blaze-shared/elements-domain';
import { toInt, type ValueWithUnitOfMeasure } from '@blaze-shared/shared-util';
import { isEqual } from '@blaze-shared/shared-util';
import { ThemesService } from '@blaze-shared/theming-data-access';

import { fromNavMenu } from '../../+state';
import { registerElementDefinition } from '../../services/element-definitions.service';
import {
  validateFontSizeWithLineHeight,
  validateLineHeightWithFontSize,
} from '../../utils/elements/validation.utils';
import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';
import { type TextAreaPropsModel } from '../text-area/text-area.element';

import { navMenuV2Migration } from './migrations/element/002-nav-menu.migration';
import { navMenuV3Migration } from './migrations/element/003-nav-menu.migration';
import { NavMenuComponent } from './nav-menu.component';
import {
  NAV_MENU_ITEM_SIZE_OPTIONS,
  NAV_MENU_TEMPLATE_OPTIONS,
  type NavMenuPropsModel,
  type NavMenuTemplate,
} from './nav-menu.models';

export type NavMenuDef = WithProps<NavMenuPropsModel>;
export type NavMenuElement = CanvasElementFrom<'navMenu'>;

// Extend the CanvasElementDefinitions type
declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    navMenu: NavMenuDef;
  }
}

const DEFAULT_WIDTH = 160;
const MIN_SIZE = 48;

registerElementDefinition({
  type: 'navMenu',
  componentType: NavMenuComponent,
  isListed: true,
  name: 'Nav Menu',
  width: { mode: 'value', value: DEFAULT_WIDTH, fixed: false },
  height: { mode: 'auto', value: MIN_SIZE, fixed: false },
  propsConfig: getPropsConfig(),
  defaults: getDefaults(),
  initial: getInitial(),
  tags: ['Navigation Header'],
  groups: ['navigation'],
  styleIncludePaths: [
    'style',
    'content.align',
    'content.template',
    'content.itemSize',
    'content.itemWidth',
    'content.iconPosition',
    'content.font',
  ],
  styleExcludePaths: ['style.menuStateTabsView', 'style.submenuStateTabsView'],
  version: 3,
  migrations: {
    2: navMenuV2Migration,
    3: navMenuV3Migration,
  },
});

function getDefaults(): PropsOf<'navMenu'> {
  return {
    version: 1,
    content: {
      template: 'default',
      itemSize: 'hug',
      itemWidth: '160px',
      iconPosition: 'left',
      align: 'left',
      direction: 'horizontal',
      autoDirection: false,
      items: undefined,
      storage: undefined,
      markSelected: true,
      sections: {
        collapsible: false,
        showDivider: false,
      },
      font: {
        style: [],
        fontSize: '$t.fontSize',
        fontFamily: '$t.fontFamily',
        lineHeight: '$t.lineHeight',
        fontWeight: '$t.fontWeight' as FontWeightOption,
      },
    },
    style: {
      general: {
        offset: { padding: '$t.spacingInput' },
      },
      labels: {
        align: 'left',
      },
      menuStateTabsView: 'default',
      submenuStateTabsView: 'default',

      default: {
        text: { color1: '$t.title', selectorType: 'solid' },
        background: { color1: '$t.fill', selectorType: 'solid' },
        border: { color1: '$t.fill', selectorType: 'none' },
        iconColor: { color1: '$t.title', selectorType: 'solid' },
      },
      hover: {
        text: { color1: '$t.title', selectorType: 'solid' },
        background: { color1: '$t.accent_a15', selectorType: 'solid' },
        border: { color1: '$t.complementary', selectorType: 'none' },
        iconColor: { color1: '$t.title', selectorType: 'solid' },
      },
      active: {
        text: { color1: '$t.accent', selectorType: 'solid' },
        background: { color1: '$t.fill', selectorType: 'solid' },
        border: { color1: '$t.fill', selectorType: 'none' },
        iconColor: { color1: '$t.accent', selectorType: 'solid' },
      },
      outline: {
        borderWidth: '4px',
        borderRadius: '$t.radius',
      },
      menu: {},
      submenu: {
        default: {
          text: { color1: '$t.title', selectorType: 'solid' },
          background: { color1: '$t.fill', selectorType: 'solid' },
          border: { color1: '$t.fill', selectorType: 'none' },
          iconColor: { color1: '$t.title', selectorType: 'solid' },
        },
        hover: {
          text: { color1: '$t.title', selectorType: 'solid' },
          background: { color1: '$t.accent_a15', selectorType: 'solid' },
          border: { color1: '$t.complementary', selectorType: 'none' },
          iconColor: { color1: '$t.title', selectorType: 'solid' },
        },
        active: {
          text: { color1: '$t.accent', selectorType: 'solid' },
          background: { color1: '$t.fill', selectorType: 'solid' },
          border: { color1: '$t.fill', selectorType: 'none' },
          iconColor: { color1: '$t.accent', selectorType: 'solid' },
        },
        background: { color1: '$t.title', selectorType: 'none' },
        outline: {
          borderWidth: '1px',
          borderRadius: '$t.radius',
        },
      },
      sections: {
        label: {
          fontSize: '$t.headerFontSize',
          fontFamily: '$t.headerFontFamily',
          lineHeight: '$t.lineHeight',
          fontWeight: '$t.headerFontWeight' as FontWeightOption,
          iconColor: { color1: '$t.title', selectorType: 'solid' },
          iconSize: '$t.headerIconSize',
          text: { color1: '$t.title', selectorType: 'solid' },
          fontStyle: '$t.headerFontStyle' as any,
        },
        sectionMargin: '8px',
        titleMargin: '8px',
        divider: {
          line: { color1: '$t.outline', selectorType: 'solid' },
          height: '1px',
        },
      },
      mobile: {
        header: { align: 'left' },
        overlay: { background: { color1: '#ffffff', selectorType: 'solid' } },
        burger: { color: { color1: '$t.value', selectorType: 'solid' } },
        enabled: true,
        label: '',
      },
    },
  };
}

function getInitial(): PartialPropsOf<'navMenu'> {
  return {
    version: 2,
    content: {
      direction: 'horizontal',
      autoDirection: true,
    },
    style: {
      general: {
        offset: { padding: '$t.spacingMenuItem' },
      },
      hover: {
        background: { color1: '$t.complementary', selectorType: 'solid' },
      },
      outline: {
        borderWidth: '1px',
      },
      submenu: {
        default: {
          text: { color1: '$t.title', selectorType: 'solid' },
          background: { color1: '$t.fill', selectorType: 'solid' },
          border: { color1: '$t.fill', selectorType: 'none' },
        },
        hover: {
          text: { color1: '$t.title', selectorType: 'solid' },
          background: { color1: '$t.complementary', selectorType: 'solid' },
          border: { color1: '$t.complementary', selectorType: 'none' },
        },
        active: {
          text: { color1: '$t.accent', selectorType: 'solid' },
          background: { color1: '$t.fill', selectorType: 'solid' },
          border: { color1: '$t.fill', selectorType: 'none' },
        },
        background: { color1: '$t.canvas', selectorType: 'solid' },
        outline: { borderWidth: '1px' },
      },
      mobile: {
        label: 'Menu',
        enabled: false,
      },
    },
  };
}

function isNotHorizontal(props?: PropsOf<'navMenu'>, context?: RenderElementContext): boolean {
  if (props?.content.autoDirection && context?.navSection) {
    return context?.navSection === 'side';
  }
  return props?.content.direction !== 'horizontal';
}

function isNotVertical(props?: PropsOf<'navMenu'>, context?: RenderElementContext): boolean {
  if (props?.content.autoDirection && context?.navSection) {
    return context?.navSection === 'header' || context?.navSection === 'bottom';
  }
  return props?.content.direction !== 'vertical';
}

function getPropsConfig(): PropertiesConfig {
  return epc.initConfig('navMenu', [
    epc.section('content', 'Content', [
      epc.subsections.subsection('content', 'Options', [
        epc.utils.observedField(
          epc.fields.selectFieldFromList('content.template', 'Style', NAV_MENU_TEMPLATE_OPTIONS, {
            allowNone: false,
            showSelection: true,
          }),
          (model: NavMenuPropsModel, value: NavMenuTemplate | undefined) => {
            const themeService = inject(ThemesService);
            const activeTheme = themeService.activeTheme?.value;
            const themedDefaults = themeService.process(getDefaults()) as PropsOf<'navMenu'>;

            // Theme should be always available at this point
            if (!activeTheme) {
              return {};
            }

            const styles: Record<string, any> = {
              /** Make icon size follow font size when changing a template */
              'style.menu.iconSize': undefined,
              'style.submenu.iconSize': undefined,
            };

            if (value === 'header') {
              if (isEqual(model.content?.font?.fontSize, themedDefaults.content?.font?.fontSize)) {
                styles['content.font.fontSize'] = '14px';
              }
              if (isEqual(model.style?.labels?.align, themedDefaults.style?.labels?.align)) {
                styles['style.labels.align'] = 'center';
              }
              if (isEqual(model.style?.default?.border, themedDefaults?.style?.default?.border)) {
                styles['style.default.border'] = { color1: '$t.outline', selectorType: 'solid' };
              }
              if (isEqual(model.style?.hover?.border, themedDefaults?.style?.hover?.border)) {
                styles['style.hover.border'] = { color1: '$t.outline', selectorType: 'solid' };
              }
              if (isEqual(model.style?.active?.text, themedDefaults?.style?.active?.text)) {
                styles['style.active.text'] = { color1: '$t.accentLabel', selectorType: 'solid' };
              }
              if (
                isEqual(model.style?.active?.background, themedDefaults.style?.active?.background)
              ) {
                styles['style.active.background'] = {
                  color1: '$t.accent',
                  selectorType: 'solid',
                };
              }
              if (isEqual(model.style?.active?.border, themedDefaults?.style?.active?.border)) {
                styles['style.active.border'] = { color1: '$t.outline', selectorType: 'solid' };
              }
              if (
                isEqual(
                  model.style?.outline?.borderWidth,
                  themedDefaults?.style?.outline?.borderWidth,
                )
              ) {
                styles['style.outline.borderWidth'] = '1px';
              }

              return { updates: styles, target: 'desktop', exclusive: true };
            }

            if (isEqual(model.content?.font?.fontSize, '14px')) {
              styles['content.font.fontSize'] = '$t.fontSize';
            }
            if (isEqual(model.style?.labels?.align, 'center')) {
              styles['style.labels.align'] = 'left';
            }
            if (
              isEqual(model.style?.default?.border, {
                color1: activeTheme.outline,
                selectorType: 'solid',
              })
            ) {
              styles['style.default.border'] = { color1: '$t.fill', selectorType: 'none' };
            }
            if (
              isEqual(model.style?.hover?.border, {
                color1: activeTheme.outline,
                selectorType: 'solid',
              })
            ) {
              styles['style.hover.border'] = { color1: '$t.complementary', selectorType: 'none' };
            }
            if (
              isEqual(model.style?.active?.text, {
                color1: activeTheme.accentLabel,
                selectorType: 'solid',
              })
            ) {
              styles['style.active.text'] = { color1: '$t.accent', selectorType: 'solid' };
            }
            if (
              isEqual(model.style?.active?.background, {
                color1: activeTheme.accent,
                selectorType: 'solid',
              })
            ) {
              styles['style.active.background'] = { color1: '$t.fill', selectorType: 'solid' };
            }
            if (
              isEqual(model.style?.active?.border, {
                color1: activeTheme.outline,
                selectorType: 'solid',
              })
            ) {
              styles['style.active.border'] = { color1: '$t.fill', selectorType: 'none' };
            }

            return { updates: styles, target: 'desktop', exclusive: true };
          },
        ),
        epc.utils.conditionalField(
          epc.fields.radiosField('content.itemSize', 'Item size', NAV_MENU_ITEM_SIZE_OPTIONS),
          'content',
          (content: NavMenuPropsModel['content']) => {
            return content.template === undefined || content.template === 'default';
          },
        ),
        epc.utils.conditionalField(
          epc.fields.sliderField('content.itemWidth', 'Width', {
            units: [{ min: 50, max: 400, step: 1, unit: 'px' }],
            hideSpinners: true,
          }),
          'content',
          (content: NavMenuPropsModel['content']) => {
            return (
              content.itemSize !== 'fixed' ||
              content.template === undefined ||
              content.template === 'default'
            );
          },
        ),
        epc.utils.conditionalField(
          epc.fields.horizontalAlignField('content.align', { label: 'Align' }),
          'content',
          (content: NavMenuPropsModel['content']) => {
            return (
              content.itemSize === 'fill' ||
              content.template === undefined ||
              content.template === 'default'
            );
          },
        ),
        epc.fields.radiosField('content.iconPosition', 'Icon position', [
          { value: 'left', label: 'Left' },
          { value: 'top', label: 'Top' },
        ]),

        epc.utils.conditionalField(
          epc.fields.directionField('content.direction', {
            beforeChange: (event): void => {
              event.preventDefault();
              const store = inject(Store);

              store.dispatch(
                fromNavMenu.requestChangeMenuDirection({
                  elementId: event.elementId,
                  direction: event.value,
                }),
              );
            },
          }),
          'content',
          ({ autoDirection }, model, params) => {
            // Always if autoDirection is disabled
            if (autoDirection !== true) {
              return false;
            }
            // Hide if element placed in nav section
            return !!params.elementContext?.navSection;
          },
        ),
        epc.utils.conditionalField(
          epc.fields.toggleField('content.markSelected', 'Show line on active item'),
          'content.template',
          (template: NavMenuTemplate | undefined, model, params) => {
            return isNotVertical(model, params.elementContext) ? template !== 'default' : false;
          },
        ),
        ...epc.utils.conditionalFields(
          [
            epc.utils.separatedField(
              epc.fields.toggleField('content.sections.collapsible', 'Allow section to collapse', {
                renderType: 'check',
              }),
              { hideSeparatorWhenHidden: true },
            ),
            epc.fields.toggleField('content.sections.showDivider', 'Add section divider', {
              renderType: 'check',
            }),
          ],
          'content.direction',
          (direction, model, params) => {
            return isNotVertical(model, params.elementContext);
          },
        ),
        ...epc.utils.conditionalFields(
          [
            epc.utils.separatedField(
              epc.fields.contentAlignField('style.mobile.header.align', 'Alignment'),
              { label: 'Mobile', hideSeparatorWhenHidden: true },
            ),
            epc.fields.toggleField('style.mobile.enabled', 'Collapse to icon'),
            ...epc.utils.conditionalFields(
              [epc.fields.textInput('style.mobile.label', 'Label')],
              'style.mobile.enabled',
              (enabled) => !enabled,
            ),
          ],
          'content.direction',
          (direction, model, params) => {
            return isNotHorizontal(model, params.elementContext);
          },
        ),
      ]),

      epc.subsections.subsection(
        'content.navMenu',
        'Menu',
        [epc.fields.navMenuItemsField('content.storage', 'Menu')],
        { hideLabel: true },
      ),
    ]),
    epc.section(
      'style',
      'Style',
      [
        epc.subsections.styleSheetSubsection(),
        epc.subsections.subsection('style.general', 'Padding', [
          epc.fields.spacingAndAlignField('style.general.offset', {
            hasPadding: true,
          }),
        ]),
        epc.subsections.subsection('style.labels', 'Menu Items', [
          ...epc.fields.fontFamilyAndWeightFields(
            'content.font.fontFamily',
            'content.font.fontWeight',
          ),
          epc.fields.fontStyleField('content.font.style', 'Style', ['line-through', 'bold']),
          epc.utils.observedField(
            epc.fields.sliderField('content.font.fontSize', 'Size', [
              { min: 8, max: 72, unit: 'px' },
            ]),
            (props: TextAreaPropsModel, value: ValueWithUnitOfMeasure) => {
              return validateLineHeightWithFontSize(props, value, 'content.font.lineHeight');
            },
          ),
          epc.utils.observedField(
            epc.fields.sliderField('content.font.lineHeight', 'Line height', [
              { min: 8, max: 72, unit: 'px' },
            ]),
            (props: TextAreaPropsModel, value: ValueWithUnitOfMeasure) => {
              return validateFontSizeWithLineHeight(props, value, 'content.font.fontSize');
            },
          ),
          epc.fields.horizontalAlignField('style.labels.align'),
        ]),

        epc.subsections.subsection(
          'style.sections',
          'Menu Sections',
          [
            ...epc.fields.fontFamilyAndWeightFields(
              'style.sections.label.fontFamily',
              'style.sections.label.fontWeight',
            ),
            epc.fields.fontStyleField('style.sections.label.fontStyle', 'Style', ['bold']),
            epc.fields.colorField('style.sections.label.text', 'Label', {
              allowGradient: false,
              allowNone: false,
            }),
            epc.utils.observedField(
              epc.fields.sliderField('style.sections.label.fontSize', 'Size', [
                { min: 8, max: 72, unit: 'px' },
              ]),
              (props: TextAreaPropsModel, value: ValueWithUnitOfMeasure) => {
                return validateLineHeightWithFontSize(
                  props,
                  value,
                  'style.sections.label.lineHeight',
                );
              },
            ),
            epc.utils.observedField(
              epc.fields.sliderField('style.sections.label.lineHeight', 'Line height', [
                { min: 8, max: 72, unit: 'px' },
              ]),
              (props: TextAreaPropsModel, value: ValueWithUnitOfMeasure) => {
                return validateFontSizeWithLineHeight(
                  props,
                  value,
                  'style.sections.label.fontSize',
                );
              },
            ),
            epc.fields.colorField('style.sections.label.iconColor', 'Icon color', {
              allowGradient: false,
              allowNone: false,
            }),
            epc.fields.sliderField('style.sections.label.iconSize', 'Icon size', {
              units: [{ min: 8, max: 72, unit: 'px' }],
              defaultValue: (model: NavMenuPropsModel) => model.style.sections.label.fontSize,
            }),
            epc.utils.separatedField(
              epc.fields.sliderField('style.sections.titleMargin', 'Title gap', [
                { min: 0, max: 72, unit: 'px' },
              ]),
            ),
            epc.fields.sliderField('style.sections.sectionMargin', 'Section gap', [
              { min: 0, max: 72, unit: 'px' },
            ]),
            ...epc.utils.conditionalFields(
              [
                epc.fields.colorField('style.sections.divider.line', 'Divider line', {
                  allowGradient: false,
                  allowNone: false,
                }),
                epc.fields.sliderField('style.sections.divider.height', 'Line weight', [
                  { min: 1, max: 10, unit: 'px' },
                ]),
              ],
              'content.sections.showDivider',
              (showDivider: boolean) => !showDivider,
            ),
          ],
          {
            hideWhen: (
              elementId,
              subsectionResolver,
              props: PropsOf<'navMenu'>,
              elementContext,
            ) => {
              return isNotVertical(props, elementContext);
            },
          },
        ),

        epc.subsections.subsection('style.menu', 'Menu', [
          epc.fields.radiosField(
            'style.menuStateTabsView',
            'Menu Tabs View',
            [
              { value: 'default', label: 'Default' },
              { value: 'hover', label: 'Hover' },
              { value: 'active', label: 'Active' },
            ],
            { hideLabel: true },
          ),

          ...epc.utils.conditionalFields(
            [
              epc.fields.colorField('style.default.text', 'Label', {
                allowGradient: false,
                allowNone: false,
              }),
              epc.fields.colorField('style.default.background', 'Background', {
                allowGradient: false,
                allowNone: true,
              }),
              epc.fields.colorField('style.default.border', 'Outline', {
                allowGradient: false,
                allowNone: true,
              }),
              epc.fields.colorField('style.default.iconColor', 'Icon color', {
                allowGradient: false,
                allowNone: false,
              }),
            ],
            'style',
            (style: NavMenuPropsModel['style']) => style.menuStateTabsView !== 'default',
          ),

          ...epc.utils.conditionalFields(
            [
              epc.fields.colorField('style.hover.text', 'Label', {
                allowGradient: false,
                allowNone: false,
              }),
              epc.fields.colorField('style.hover.background', 'Background', {
                allowGradient: false,
                allowNone: true,
              }),
              epc.fields.colorField('style.hover.border', 'Outline', {
                allowGradient: false,
                allowNone: true,
              }),
              epc.fields.colorField('style.hover.iconColor', 'Icon color', {
                allowGradient: false,
                allowNone: false,
              }),
            ],
            'style',
            (style: NavMenuPropsModel['style']) => style.menuStateTabsView !== 'hover',
          ),

          ...epc.utils.conditionalFields(
            [
              epc.fields.colorField('style.active.text', 'Label', {
                allowGradient: false,
                allowNone: false,
              }),
              epc.fields.colorField('style.active.background', 'Background', {
                allowGradient: false,
                allowNone: true,
              }),
              epc.fields.colorField('style.active.border', 'Outline', {
                allowGradient: false,
                allowNone: true,
              }),
              epc.fields.colorField('style.active.iconColor', 'Icon color', {
                allowGradient: false,
                allowNone: false,
              }),
            ],
            'style',
            (style: NavMenuPropsModel['style']) => style.menuStateTabsView !== 'active',
          ),

          epc.utils.separatedField(
            epc.fields.sliderField('style.menu.iconSize', 'Icon size', {
              units: [{ min: 8, max: 72, unit: 'px' }],
              defaultValue: (model: NavMenuPropsModel) => {
                if (model.content.template === 'header') {
                  const iFontSize = toInt(model.content.font.fontSize, 0);
                  return `${iFontSize + 10}px`;
                }

                return model.content.font.fontSize;
              },
            }),
            { hideSeparatorWhenHidden: true },
          ),
        ]),

        epc.subsections.subsection('style.submenu', 'Sub-Menu', [
          epc.fields.radiosField(
            'style.submenuStateTabsView',
            'Submenu Tabs View',
            [
              { value: 'default', label: 'Default' },
              { value: 'hover', label: 'Hover' },
              { value: 'active', label: 'Active' },
            ],
            { hideLabel: true },
          ),

          ...epc.utils.conditionalFields(
            [
              epc.fields.colorField('style.submenu.default.text', 'Label', {
                allowGradient: false,
                allowNone: false,
              }),
              epc.fields.colorField('style.submenu.default.background', 'Background', {
                allowGradient: false,
                allowNone: true,
              }),
              epc.fields.colorField('style.submenu.default.border', 'Outline', {
                allowGradient: false,
                allowNone: true,
              }),
              epc.fields.colorField('style.submenu.default.iconColor', 'Icon color', {
                allowGradient: false,
                allowNone: false,
              }),
            ],
            'style',
            (style: NavMenuPropsModel['style']) => style.submenuStateTabsView !== 'default',
          ),

          ...epc.utils.conditionalFields(
            [
              epc.fields.colorField('style.submenu.hover.text', 'Label', {
                allowGradient: false,
                allowNone: false,
              }),
              epc.fields.colorField('style.submenu.hover.background', 'Background', {
                allowGradient: false,
                allowNone: true,
              }),
              epc.fields.colorField('style.submenu.hover.border', 'Outline', {
                allowGradient: false,
                allowNone: true,
              }),
              epc.fields.colorField('style.submenu.hover.iconColor', 'Icon color', {
                allowGradient: false,
                allowNone: false,
              }),
            ],
            'style',
            (style: NavMenuPropsModel['style']) => style.submenuStateTabsView !== 'hover',
          ),

          ...epc.utils.conditionalFields(
            [
              epc.fields.colorField('style.submenu.active.text', 'Label', {
                allowGradient: false,
                allowNone: false,
              }),
              epc.fields.colorField('style.submenu.active.background', 'Background', {
                allowGradient: false,
                allowNone: true,
              }),
              epc.fields.colorField('style.submenu.active.border', 'Outline', {
                allowGradient: false,
                allowNone: true,
              }),
              epc.fields.colorField('style.submenu.active.iconColor', 'Icon color', {
                allowGradient: false,
                allowNone: false,
              }),
            ],
            'style',
            (style: NavMenuPropsModel['style']) => style.submenuStateTabsView !== 'active',
          ),

          epc.utils.separatedField(
            epc.fields.colorField('style.submenu.background', 'Sub-Menu Background', {
              allowGradient: false,
              allowNone: true,
            }),
            { hideSeparatorWhenHidden: true },
          ),
          epc.fields.sliderField('style.submenu.iconSize', 'Icon size', {
            units: [{ min: 8, max: 72, unit: 'px' }],
            defaultValue: (model: NavMenuPropsModel) => model.content.font.fontSize,
          }),
        ]),

        epc.subsections.subsection('style.mobile', 'Mobile', [
          epc.fields.colorField('style.mobile.burger.color', 'Icon', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.colorField('style.mobile.overlay.background', 'Background', {
            allowGradient: false,
            allowNone: true,
          }),
        ]),
        epc.subsections.subsection('style.outline', 'Outline', [
          epc.fields.sliderField('style.outline.borderWidth', 'Width', [
            {
              min: 0,
              max: 5,
              step: 1,
              unit: 'px',
            },
          ]),
          epc.fields.radiusField('style.outline.borderRadius', 'Radius'),
        ]),
      ],
      true,
    ),
  ]);
}
